import React, { useContext, useEffect, useRef, useState } from 'react';

import cogTokens from '@amzn/meridian-tokens/base/icon/cog';
import externalTokens from '@amzn/meridian-tokens/base/icon/export-small';
import gridTokens from '@amzn/meridian-tokens/base/icon/grid';
import helpIconTokens from '@amzn/meridian-tokens/base/icon/help';
import starTokens from '@amzn/meridian-tokens/base/icon/star';
import starFilledTokens from '@amzn/meridian-tokens/base/icon/star-filled';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Masthead, { MastheadLink, MastheadTitle } from '@amzn/meridian/masthead';
import Popover from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Text from '@amzn/meridian/text';
import { ProductPopover } from '@amzn/music-paper-ui-components';

import { useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import Logo from 'src/images/origami_logo.svg';
import { getStage } from 'src/util/config';
import { addFavorite, isFavorite, removeFavorite } from 'src/util/favorites';
import { SearchResult, searchSite } from 'src/util/search';

import './TopBar.scss';

const mapStageForPopover = (stage: string): 'dev' | 'beta' | 'gamma' | 'prod' => {
    if (stage === 'test' || stage === 'personal') return 'beta';
    return stage as 'dev' | 'beta' | 'gamma' | 'prod';
};

export const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
};

export const TopBar: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const basePath = `/${location.pathname.split('/')[1]}`;

    const { royaltyManagerFeatureEnabled, entities, userInfo } = useContext(AppContext);

    const appsButtonRef = useRef();
    const [appsMenuOpen, setAppsMenuOpen] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    const helpButtonRef = useRef();
    const [helpMenuOpen, setHelpMenuOpen] = useState(false);
    const [isPageFavorited, setIsPageFavorited] = useState(false);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setShowResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        const results = searchSite(value, entities);
        setSearchResults(results);
        setShowResults(results.length > 0);
    };

    const handleSearchResultClick = (path: string) => {
        history.push(path);
        setSearchQuery('');
        setShowResults(false);
    };

    React.useEffect(() => {
        setIsPageFavorited(isFavorite(location.pathname));
    }, [location.pathname]);

    const getPageTitle = () => {
        const path = location.pathname;
        if (path === ROUTES.DEALS.INDEX) return 'Labels | Deals';
        if (path === ROUTES.VENDORS.INDEX) return 'Labels | Vendors';
        if (path === ROUTES.RATE_CARDS.INDEX) return 'Labels | Contracts';
        if (path === ROUTES.REPORTING.INDEX) return 'Labels | Reporting';
        if (path === ROUTES.AGREEMENTS.INDEX) return 'Labels | Agreements';
        if (path === ROUTES.ANALYTICS.LICENSING_KPI.AMU_METRICS) return 'Analytics | AMU Metrics';
        if (path === ROUTES.ANALYTICS.LICENSING_KPI.PRIME_METRICS) return 'Analytics | Prime Metrics';
        if (path === ROUTES.ANALYTICS.LICENSING_KPI.AMF_METRICS) return 'Analytics | AMF Metrics';
        if (path === ROUTES.PAPER_PLANS.INDEX) return 'Resources | PAPER Plans';
        if (path === ROUTES.PLAN_WEIGHTS.INDEX) return 'Resources | Plan Weights';
        if (path === ROUTES.PAPER_PLAN_MAPPINGS.INDEX) return 'Resources | PAPER Plan Mappings';
        if (path === ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX) return 'Resources | Base Plan Launch Dates';
        if (path === ROUTES.SERVICE_LAUNCHES.INDEX) return 'Resources | Service Launch Dates';
        if (path === ROUTES.CURRENCY_CONTENT_ACQUISITION_ENTITY.INDEX) return 'Resources | Territory Defaults';
        if (path === ROUTES.BUNDLE_PRICING.INDEX) return 'Resources | Bundle Pricing';
        if (path === ROUTES.COMPARE.CONTRACTS) return 'Resources | Compare Contracts';
        if (path === ROUTES.COMPARE.DEALS) return 'Resources | Compare Deals';
        if (path === ROUTES.AI.INDEX) return 'AI Chat';
        if (path === ROUTES.ROYALTY_MANAGER.INDEX) return 'Royalty Manager';
        return null;
    };

    const toggleFavorite = () => {
        const title = getPageTitle();
        if (!title) return;

        if (isPageFavorited) {
            removeFavorite(location.pathname);
            setIsPageFavorited(false);
        } else {
            addFavorite({ title, path: location.pathname });
            setIsPageFavorited(true);
        }
    };

    const userGuideLink = 'https://w.amazon.com/bin/view/Amazon_Music/PAPER/Products/ContractManager/UserGuide';
    const simLink =
        'https://t.corp.amazon.com/create/options?category=Digital%20Media%20Technology&type=Music%20PAPER%20-%20Royalty%20Administration&item=UI%20Issues&group=Music%20PAPER%20-%20RAD';

    const getStageName = () => {
        switch (getStage()) {
            case 'test':
            case 'beta':
                return <Text type="b200">BETA</Text>;
            case 'gamma':
                return <Text type="b200">GAMMA</Text>;
            case 'prod':
                return null;
            case 'personal':
                return <Text type="b200">Personal Dev</Text>;
            default:
                return null;
        }
    };

    return (
        <div className="top-bar">
            <Masthead size="large">
                <MastheadTitle href={ROUTES.HOME} onClick={history.push}>
                    <Row spacing="200">
                        <img src={Logo} alt="Logo" />
                        {getStageName()}
                    </Row>
                </MastheadTitle>
                <MastheadLink>
                    <Button
                        type="icon"
                        ref={appsButtonRef}
                        onClick={() => setAppsMenuOpen(true)}
                        data-testid="top-bar-apps-btn"
                    >
                        <Icon tokens={gridTokens} />
                    </Button>
                </MastheadLink>
                <ProductPopover
                    anchorNode={appsButtonRef.current}
                    open={appsMenuOpen}
                    onClose={() => setAppsMenuOpen(false)}
                    position="bottom"
                    theme="light"
                    stage={mapStageForPopover(getStage())}
                />
                {basePath !== ROUTES.HOME && (
                    <div className="top-bar-search" ref={searchRef}>
                        <SearchField
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onFocus={() => searchQuery && setShowResults(true)}
                            onSubmit={() => {}}
                        />
                        {showResults && searchResults.length > 0 && (
                            <div className="top-bar-search-results">
                                {searchResults.map((result, index) => (
                                    <div
                                        key={index}
                                        className="search-result-item"
                                        onClick={() => handleSearchResultClick(result.path)}
                                    >
                                        <div className="search-result-main">
                                            <span className="search-result-title">{result.title}</span>
                                            {result.subtitle && (
                                                <span className="search-result-subtitle">{result.subtitle}</span>
                                            )}
                                        </div>
                                        <span className="search-result-category">{result.category}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {(userInfo.firstName || userInfo.username) && (
                    <Text type="h300" data-testid="top-bar-greeting">
                        {getGreeting()}, {userInfo.firstName || userInfo.username}!
                    </Text>
                )}
                <Button
                    type="icon"
                    ref={helpButtonRef}
                    onClick={() => setHelpMenuOpen(true)}
                    data-testid="top-bar-help-btn"
                >
                    <Icon tokens={helpIconTokens} />
                </Button>
                <Popover
                    anchorNode={helpButtonRef.current}
                    open={helpMenuOpen}
                    position={'bottom'}
                    onClose={() => setHelpMenuOpen(false)}
                >
                    <Column alignmentHorizontal="start" spacingInset="300">
                        <Row>
                            <Link href={ROUTES.RELEASE_NOTES.INDEX} type="secondary">
                                Release Notes
                            </Link>
                        </Row>
                        <Row>
                            <Link href={userGuideLink} target="_blank" type="secondary">
                                User Guide <Icon tokens={externalTokens} />
                            </Link>
                        </Row>
                        <Row>
                            <Link href={simLink} target="_blank" type="secondary">
                                Submit SIM <Icon tokens={externalTokens} />
                            </Link>
                        </Row>
                    </Column>
                </Popover>
                {getPageTitle() && (
                    <Button type="icon" onClick={toggleFavorite} data-testid="top-bar-favorite-btn">
                        <Icon tokens={isPageFavorited ? starFilledTokens : starTokens} />
                    </Button>
                )}
                <Button
                    type="icon"
                    onClick={() => history.push(ROUTES.SETTINGS.INDEX)}
                    data-testid="top-bar-setting-btn"
                >
                    <Icon tokens={cogTokens} />
                </Button>
            </Masthead>
        </div>
    );
};
